import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { api, errText } from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { PatientLayout, HelpDrawer, PatientLoading, PatientError } from '@/components/PatientLayout';
import { Lock, ArrowLeft, ArrowRight, CheckCircle2, HelpCircle, Loader2, Check, ChevronDown, Pencil, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { EmployerFormScan } from '@/components/EmployerFormScan';
import { useLang, getLang, setLang } from '@/lib/i18n';
import { cn } from '@/lib/utils';
import { STEPS, REVIEW_STEP, maxStep, initialStep, stepComplete } from '@/lib/patientSteps';

const FIELD_CLS = 'h-14 text-[17px] rounded-xl bg-card';

function Field({ label, help, invalid, children }) {
  const { t } = useLang();
  // Associate the label with its control, and wire aria-invalid / -describedby, so
  // a screen reader announces the field name and its error (GAPS F22).
  const autoId = React.useId();
  const controlId = (React.isValidElement(children) && children.props.id) || autoId;
  const errId = `${controlId}-err`;
  const control = React.isValidElement(children)
    ? React.cloneElement(children, {
        id: controlId,
        'aria-invalid': invalid || undefined,
        'aria-describedby': invalid ? errId : children.props['aria-describedby'],
      })
    : children;
  return (
    <div className="space-y-1.5">
      <Label htmlFor={controlId} className="text-[15px] font-medium">{label}</Label>
      {control}
      {help && !invalid && <p className="text-sm text-muted-foreground leading-6">{help}</p>}
      {invalid && <p id={errId} className="text-sm text-destructive" role="alert">{t('form.needed')}</p>}
    </div>
  );
}

// "I don't know — ask my office" gap-fill toggle (SME feature).
function AskOfficeToggle({ checked, onToggle, testId }) {
  const { t } = useLang();
  return (
    <button
      type="button"
      onClick={onToggle}
      data-testid={testId}
      aria-pressed={checked}
      className={cn(
        'mt-2 w-full min-h-[48px] flex items-center gap-2.5 rounded-xl border px-3.5 py-2.5 text-left text-[15px] transition-colors',
        checked
          ? 'border-primary bg-accent text-accent-foreground'
          : 'border-border bg-card text-muted-foreground hover:bg-muted'
      )}
    >
      <HelpCircle className="h-5 w-5 shrink-0" />
      <span className="flex-1">{t('form.askOffice')}</span>
      {checked && <CheckCircle2 className="h-5 w-5 text-primary shrink-0" />}
    </button>
  );
}

// Two fields. Email and phone moved to Review (they are only needed for the
// "it's ready" message), and the consent switch became the Continue button
// itself — see the consent line in the footer (review 2026-09-06 §2).
const hasContact = (form) => !!((form.patient_email || '').trim() || (form.patient_phone || '').trim());

function PersonalStep({ form, set, tried }) {
  const { t } = useLang();
  return (
    <div className="space-y-5" data-testid="patient-form-step-personal">
      <Field label={t('personal.fullName')} invalid={tried && !form.full_name}>
        <Input className={FIELD_CLS} value={form.full_name || ''} onChange={(e) => set('full_name', e.target.value)} autoComplete="name" data-testid="patient-field-full-name" />
      </Field>
      <Field label={t('personal.dob')} invalid={tried && !form.dob}>
        <Input type="date" className={FIELD_CLS} value={form.dob || ''} onChange={(e) => set('dob', e.target.value)} data-testid="patient-field-dob" />
      </Field>
    </div>
  );
}

function EmploymentStep({ form, set, tried, isUnknown, toggleUnknown }) {
  const { t } = useLang();
  const contactUnknown = isUnknown('employer_contact');
  return (
    <div className="space-y-5" data-testid="patient-form-step-employment">
      <Field label={t('employment.where')} help={t('employment.whereHelp')} invalid={tried && !form.employer_name}>
        <Input className={FIELD_CLS} value={form.employer_name || ''} onChange={(e) => set('employer_name', e.target.value)} autoComplete="organization" data-testid="patient-field-employer-name" />
      </Field>
      <Field label={t('employment.jobTitle')} invalid={tried && !form.job_title}>
        <Input className={FIELD_CLS} value={form.job_title || ''} onChange={(e) => set('job_title', e.target.value)} autoComplete="organization-title" data-testid="patient-field-job-title" />
      </Field>
      <Field label={t('employment.hrEmail')} help={t('employment.hrEmailHelp')}>
        <Input
          type="email"
          inputMode="email"
          autoComplete="off"
          className={FIELD_CLS}
          value={form.employer_email || ''}
          onChange={(e) => set('employer_email', e.target.value)}
          placeholder="hr@company.com"
          data-testid="patient-field-employer-email"
        />
      </Field>
      <Field
        label={t('employment.hrContact')}
        help={t('employment.hrContactHelp')}
        invalid={tried && !form.employer_contact && !form.employer_email && !contactUnknown}
      >
        <Input
          className={FIELD_CLS}
          value={form.employer_contact || ''}
          onChange={(e) => set('employer_contact', e.target.value)}
          disabled={contactUnknown}
          placeholder={contactUnknown ? t('form.officeWillFill') : ''}
          data-testid="patient-field-employer-contact"
        />
        <AskOfficeToggle checked={contactUnknown} onToggle={() => toggleUnknown('employer_contact')} testId="patient-unknown-employer-contact" />
      </Field>
    </div>
  );
}

function LeaveStep({ form, set, tried, isUnknown, toggleUnknown }) {
  const { t } = useLang();
  const endUnknown = isUnknown('leave_end');
  return (
    <div className="space-y-5" data-testid="patient-form-step-leave">
      <Field label={t('leave.why')} help={t('leave.whyHelp')} invalid={tried && !form.reason_for_leave}>
        <Textarea rows={4} className="text-[17px] rounded-xl bg-card leading-7" value={form.reason_for_leave || ''} onChange={(e) => set('reason_for_leave', e.target.value)} data-testid="patient-field-reason" />
      </Field>
      <Field label={t('leave.start')} invalid={tried && !form.leave_start}>
        <Input type="date" className={FIELD_CLS} value={form.leave_start || ''} onChange={(e) => set('leave_start', e.target.value)} data-testid="patient-field-leave-start" />
      </Field>
      <Field
        label={t('leave.back')}
        help={t('leave.backHelp')}
        invalid={tried && !form.leave_end && !endUnknown}
      >
        <Input
          type="date"
          className={FIELD_CLS}
          value={form.leave_end || ''}
          onChange={(e) => set('leave_end', e.target.value)}
          disabled={endUnknown}
          data-testid="patient-field-leave-end"
        />
        <AskOfficeToggle checked={endUnknown} onToggle={() => toggleUnknown('leave_end')} testId="patient-unknown-leave-end" />
      </Field>
    </div>
  );
}

// The last screen: the answers, how to reach the patient, the optional
// employer form, and what the one button does. Review and Pay used to be two
// full screens and the scan its own mandatory step (review 2026-09-06 §2).
function ReviewStep({ form, set, tried, isUnknown, goto, token, employerForm, onEmployerFormChanged, scanRef, price, paymentRequired, turnaroundDays }) {
  const { t } = useLang();
  const askOffice = <span className="italic text-primary">{t('review.officeFills')}</span>;
  const rows = [
    {
      key: 'personal', title: t('review.about'), step: 0, testId: 'patient-review-edit-personal',
      lines: [form.full_name, t('review.born', { dob: form.dob || '—' })],
    },
    {
      key: 'employment', title: t('review.job'), step: 1, testId: 'patient-review-edit-employment',
      lines: [`${form.employer_name || '—'} — ${form.job_title || '—'}`, form.employer_email || '', isUnknown('employer_contact') ? askOffice : (form.employer_contact || (form.employer_email ? '' : '—'))].filter((l) => l !== ''),
    },
    {
      key: 'leave', title: t('review.timeOff'), step: 2, testId: 'patient-review-edit-leave',
      lines: [<span key="dates">{form.leave_start || '—'} → {isUnknown('leave_end') ? askOffice : (form.leave_end || '—')}</span>, form.reason_for_leave],
    },
  ];
  const hasEmployerForm = !!employerForm?.status;
  return (
    <div className="space-y-3" data-testid="patient-form-step-review">
      {rows.map((r) => (
        <div key={r.key} className="rounded-2xl border bg-card p-4">
          <div className="flex items-center justify-between">
            <div className="font-semibold text-[16px]">{r.title}</div>
            <Button variant="ghost" size="sm" className="h-10 rounded-xl text-primary" onClick={() => goto(r.step)} data-testid={r.testId}>
              <Pencil className="h-4 w-4 mr-1" /> {t('review.edit')}
            </Button>
          </div>
          <div className="text-[15px] text-muted-foreground leading-7">
            {r.lines.map((l, i) => <div key={i}>{l || '—'}</div>)}
          </div>
        </div>
      ))}

      <div className="rounded-2xl border bg-card p-4 space-y-4" data-testid="patient-review-contact">
        <div>
          <div className="font-semibold text-[16px]">{t('review.contactTitle')}</div>
          <div className="text-sm text-muted-foreground leading-6">{t('review.contactHint')}</div>
        </div>
        <Field label={t('personal.email')} invalid={tried && !hasContact(form)}>
          <Input type="email" className={FIELD_CLS} value={form.patient_email || ''} onChange={(e) => set('patient_email', e.target.value)} autoComplete="email" placeholder="you@example.com" data-testid="patient-field-email" />
        </Field>
        <Field label={t('personal.phone')} invalid={tried && !hasContact(form)}>
          <Input type="tel" className={FIELD_CLS} value={form.patient_phone || ''} onChange={(e) => set('patient_phone', e.target.value)} autoComplete="tel" placeholder="+1 555 123 4567" data-testid="patient-field-phone" />
        </Field>
      </div>

      <Collapsible defaultOpen={hasEmployerForm}>
        <div className="rounded-2xl border bg-card p-4 space-y-3" data-testid="patient-review-employer-form">
          <CollapsibleTrigger asChild>
            <button type="button" className="w-full flex items-center gap-3 text-left" data-testid="patient-employer-form-toggle">
              <div className="h-10 w-10 rounded-xl bg-accent text-accent-foreground grid place-items-center shrink-0"><FileText className="h-5 w-5" /></div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-[16px]">{t('review.employerFormTitle')}</div>
                <div className="text-sm text-muted-foreground leading-6">{t('review.employerFormHint')}</div>
              </div>
              <ChevronDown className="h-5 w-5 text-muted-foreground shrink-0" />
            </button>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <EmployerFormScan mode="patient" token={token} employerForm={employerForm} onChanged={onEmployerFormChanged} scanRef={scanRef} />
          </CollapsibleContent>
        </div>
      </Collapsible>

      <div className="rounded-2xl border bg-card p-5" data-testid={paymentRequired ? 'patient-review-fee' : 'patient-no-fee-notice'}>
        {paymentRequired ? (
          <>
            <div className="text-sm text-muted-foreground">{t('pay.feeLabel')}</div>
            <div className="text-4xl font-bold tracking-tight tabular-nums mt-1" data-testid="patient-payment-price">${price?.toFixed?.(2)}</div>
            <p className="text-sm text-muted-foreground leading-6 mt-2">{t('pay.covers')}</p>
          </>
        ) : (
          // ADR 0001 Mode 3 / ADR 0004 Plan C: the office bills the patient itself,
          // or nobody does — either way there is no card here.
          <>
            <div className="text-sm text-muted-foreground">{t('submit.readyLabel')}</div>
            <div className="text-[17px] font-semibold tracking-tight mt-1">{t('submit.noFee')}</div>
            <p className="text-sm text-muted-foreground leading-6 mt-2">{t('submit.covers')}</p>
          </>
        )}
        <div className="rounded-xl bg-secondary px-3.5 py-2.5 text-sm text-secondary-foreground mt-3" data-testid="patient-payment-turnaround">
          {t('pay.turnaround', { days: turnaroundDays })}
        </div>
        {paymentRequired && (
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted-foreground mt-3">
            <span className="inline-flex items-center gap-1.5"><Lock className="h-4 w-4" /> {t('pay.secure')}</span>
            <span>·</span>
            <span>{t('pay.noCard')}</span>
            <span>·</span>
            <span>{t('pay.stripe')}</span>
          </div>
        )}
      </div>

      <p className="text-sm text-muted-foreground leading-6">{t('review.doctorNote')}</p>
    </div>
  );
}

function CurrentStep(props) {
  const { step, form, set, tried, isUnknown, toggleUnknown } = props;
  if (step === 0) return <PersonalStep form={form} set={set} tried={tried} />;
  if (step === 1) return <EmploymentStep form={form} set={set} tried={tried} isUnknown={isUnknown} toggleUnknown={toggleUnknown} />;
  if (step === 2) return <LeaveStep form={form} set={set} tried={tried} isUnknown={isUnknown} toggleUnknown={toggleUnknown} />;
  if (step === REVIEW_STEP) return <ReviewStep {...props} />;
  return null;
}

export default function PatientForm() {
  const { t, lang } = useLang();
  const { token } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const canceledOnce = useRef(new URLSearchParams(window.location.search).get('canceled') === '1');
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({});
  const [saveState, setSaveState] = useState('idle'); // idle | saving | saved
  const [tried, setTried] = useState(false);
  const [paying, setPaying] = useState(false);
  const saveTimer = useRef(null);
  const scanRef = useRef(null);   // the employer-form card's pending work (EmployerFormScan)
  // Stable ref to t: loadInitial must NOT re-run on language switch (it would
  // re-fetch and clobber in-progress form state), but its one-time toast
  // should still use the current language.
  const tRef = useRef(t);
  tRef.current = t;

  const loadInitial = useCallback(async () => {
    let r;
    try {
      r = await api.get(`/patient/by-token/${token}`);
    } catch (e) {
      // An expired or unknown token used to spin for ever here (full-flow review
      // 2026-09-06 §1.5) — the 410/404 never reached the screen.
      setError(e?.response?.data?.detail || tRef.current('layout.linkNotFound'));
      return;
    }
    setData(r.data);
    const pf = r.data.submission?.patient_form || {};
    setForm(pf);
    // Restore the language this patient chose last time
    if (pf.preferred_language && pf.preferred_language !== getLang()) setLang(pf.preferred_language);
    if (r.data.submission.status !== 'draft_patient') {
      navigate(`/p/${token}/status`);
      return;
    }
    // Returning from a canceled Stripe checkout -> land back on Review, where the
    // button is. Otherwise resume where the answers (and the URL) say we were.
    let start = initialStep(pf, parseInt(new URLSearchParams(window.location.search).get('step'), 10));
    if (canceledOnce.current) {
      canceledOnce.current = false;
      start = REVIEW_STEP;
      toast.info(tRef.current('pay.canceled'));
    }
    setStep(start);
    setSearchParams({ step: String(start) }, { replace: true });
  }, [token, navigate, setSearchParams]);

  // Once per link, on mount — deliberately NOT keyed on `loadInitial`. React
  // Router hands out a new `setSearchParams` on every URL change, so keying on
  // the callback re-ran this on every step: it re-fetched the form and, when the
  // 600 ms autosave had not fired yet, overwrote what the patient had just typed
  // and bounced them back a step (found driving the form, 2026-09-06).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadInitial(); }, [token]);

  // Browser back/forward moves through steps, not out of the form: `goto` pushes
  // `?step=n`, and this follows the URL back. Clamped to what the answers allow.
  useEffect(() => {
    if (!data) return;
    const s = parseInt(searchParams.get('step'), 10);
    if (Number.isInteger(s) && s >= 0 && s < STEPS.length && s !== step) {
      setTried(false);
      setStep(Math.min(s, maxStep(form)));
      window.scrollTo({ top: 0 });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Persist the patient's language so staff can see it and future visits restore it.
  useEffect(() => {
    if (!data) return;
    if ((form.preferred_language || 'en') !== lang) {
      apply({ ...form, preferred_language: lang });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang, data]);

  const apply = (next) => {
    setForm(next);
    setSaveState('saving');
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      try {
        await api.put(`/patient/by-token/${token}`, { patient_form: next });
        setSaveState('saved');
      } catch (e) {
        console.error(e);
        setSaveState('idle');
      }
    }, 600);
  };

  const set = (k, v) => apply({ ...form, [k]: v });

  const isUnknown = (k) => (form.unknown_fields || []).includes(k);
  const toggleUnknown = (k) => {
    const list = form.unknown_fields || [];
    const has = list.includes(k);
    const next = {
      ...form,
      unknown_fields: has ? list.filter((x) => x !== k) : [...list, k],
    };
    if (!has) next[k] = '';
    apply(next);
  };

  const refreshEmployerForm = useCallback(async () => {
    try {
      const r = await api.get(`/patient/by-token/${token}`);
      setData((d) => ({ ...d, submission: r.data.submission }));
    } catch (e) { console.error(e); }
  }, [token]);

  const goto = (s) => {
    setTried(false);
    setStep(s);
    setSearchParams({ step: String(s) }); // pushes history, so back = previous step
    window.scrollTo({ top: 0 });
  };

  const next = () => {
    if (!stepComplete(form, step)) {
      setTried(true);
      toast.error(t('form.almostThere'));
      return;
    }
    // Continuing past "About you" IS the consent to share with the practice —
    // the line under the button says so. Recorded once, with when.
    if (step === 0 && !form.consent) apply({ ...form, consent: true, consent_at: new Date().toISOString() });
    goto(Math.min(step + 1, REVIEW_STEP));
  };
  const prev = () => goto(Math.max(step - 1, 0));

  // Sending is consent too. Continue past step 1 records it for a form started
  // today; a draft begun before the switch was removed may have landed past
  // step 1 with `consent: false`, so the send records it as well.
  const withConsent = () => (form.consent ? form : { ...form, consent: true, consent_at: new Date().toISOString() });

  // The employer-form card holds two things the send would otherwise lose:
  // photos still in its tray (never scanned) and answers typed but not yet saved.
  const employerFormReady = async () => {
    const scan = scanRef.current;
    if (scan?.trayCount > 0) { toast.error(t('scan.trayUnscanned')); return false; }
    try {
      await scan?.flush?.();
    } catch (e) {
      toast.error(errText(e, t('scan.answersError')));
      return false;
    }
    return true;
  };

  const pay = async () => {
    if (!hasContact(form)) { setTried(true); toast.error(t('review.contactRequired')); return; }
    setPaying(true);
    if (!(await employerFormReady())) { setPaying(false); return; }
    try {
      // Save the latest answers, then hand off to Stripe's hosted checkout.
      const final = withConsent();
      setForm(final);
      await api.put(`/patient/by-token/${token}`, { patient_form: final });
      const r = await api.post(`/patient/by-token/${token}/checkout/session`, {
        origin_url: window.location.origin,
      });
      if (r.data?.url) {
        window.location.href = r.data.url; // -> Stripe; returns to /p/{token}/status?session_id=...
        return;
      }
      throw new Error('No checkout URL received');
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('pay.error'));
      setPaying(false);
    }
  };

  // ADR 0001 Mode 3 / ADR 0004 Plan C: no payment step at all — the form goes
  // straight into the staff queue.
  const submitWithoutPayment = async () => {
    if (!hasContact(form)) { setTried(true); toast.error(t('review.contactRequired')); return; }
    setPaying(true);
    if (!(await employerFormReady())) { setPaying(false); return; }
    try {
      const final = withConsent();
      setForm(final);
      await api.put(`/patient/by-token/${token}`, { patient_form: final });
      await api.post(`/patient/by-token/${token}/submit`);
      navigate(`/p/${token}/status`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('submit.error'));
      setPaying(false);
    }
  };

  const progress = useMemo(() => Math.round((step / REVIEW_STEP) * 100), [step]);

  if (error) return <PatientError message={error} />;
  if (!data) return <PatientLoading />;
  const price = data.price;
  // Default true: an older backend that doesn't send the flag still gets payment.
  const paymentRequired = data.payment_required !== false;
  const finish = paymentRequired ? pay : submitWithoutPayment;
  const stepKey = STEPS[step].key;
  const turnaroundDays = data.practice?.settings?.expected_turnaround_days ?? 3;
  const practiceName = data.practice?.name || t('welcome.yourCareTeam');

  const savePill = saveState !== 'idle' && (
    <span
      aria-live="polite"
      data-testid="patient-form-autosave-indicator"
      className="inline-flex items-center gap-1.5 rounded-full bg-secondary px-3 py-1.5 text-xs font-medium text-secondary-foreground"
    >
      {saveState === 'saving'
        ? <><Loader2 className="h-3 w-3 animate-spin" /> {t('form.saving')}</>
        : <><Check className="h-3 w-3 text-primary" /> {t('form.saved')}</>}
    </span>
  );

  const footer = (
    <div className="space-y-2.5">
      {step === 0 && (
        <p className="text-xs text-muted-foreground leading-5" data-testid="patient-consent-line">
          {t('form.consentLine', { practice: practiceName })}{' '}
          <a href="/hipaa" target="_blank" rel="noopener noreferrer" className="text-primary font-medium underline" data-testid="patient-consent-hipaa-link">
            {t('personal.learnMore')}
          </a>
        </p>
      )}
      <div className="flex gap-2.5">
        {step > 0 && (
          <Button variant="outline" className="h-14 w-14 rounded-xl p-0 shrink-0" onClick={prev} aria-label={t('form.back')} data-testid="patient-form-back-button">
            <ArrowLeft className="h-5 w-5" />
          </Button>
        )}
        {step < REVIEW_STEP && (
          <Button className="h-14 flex-1 rounded-xl text-base font-semibold" onClick={next} data-testid="patient-form-continue-button">
            {t('form.continue')} <ArrowRight className="h-5 w-5 ml-1.5" />
          </Button>
        )}
        {step === REVIEW_STEP && (
          <Button
            className="h-14 flex-1 rounded-xl text-base font-semibold"
            onClick={finish}
            disabled={paying}
            data-testid={paymentRequired ? 'stripe-checkout-pay-button' : 'patient-submit-no-payment-button'}
          >
            {paymentRequired
              ? (paying ? t('pay.processing') : t('pay.button', { price: price?.toFixed?.(2) }))
              : (paying ? t('submit.processing') : t('submit.button'))}
          </Button>
        )}
      </div>
    </div>
  );

  return (
    <PatientLayout
      practice={data.practice}
      right={<div className="flex items-center gap-2">{savePill}<HelpDrawer practice={data.practice} token={token} triggerTestId="patient-form-need-help-button" /></div>}
      footer={footer}
    >
      <div className="mb-5">
        <div className="flex items-center justify-between text-sm text-muted-foreground mb-2">
          <span data-testid="patient-form-step-indicator">{t('form.step', { n: step + 1, total: STEPS.length })}</span>
          <span className="tabular-nums">{progress}%</span>
        </div>
        <Progress value={progress} className="h-2" aria-label={t('form.progressLabel')} data-testid="patient-form-progress" />
        <h1 className="text-2xl font-bold tracking-tight mt-4" data-testid="patient-form-step-title">{t(`steps.${stepKey}.title`)}</h1>
        <p className="text-[15px] text-muted-foreground leading-6 mt-1">{t(`steps.${stepKey}.hint`)}</p>
      </div>

      {/* Re-mounted per step so the entrance animation replays (tailwindcss-animate). */}
      <div key={step} className="animate-in fade-in slide-in-from-bottom-2 duration-200">
        <CurrentStep
          step={step}
          form={form}
          set={set}
          tried={tried}
          isUnknown={isUnknown}
          toggleUnknown={toggleUnknown}
          price={price}
          paymentRequired={paymentRequired}
          token={token}
          employerForm={data.submission?.employer_form}
          onEmployerFormChanged={refreshEmployerForm}
          scanRef={scanRef}
          goto={goto}
          turnaroundDays={turnaroundDays}
        />
      </div>
    </PatientLayout>
  );
}
